import React, { useState, useEffect } from "react";
import { PrimaryButton, TextField, DatePicker, Dropdown } from "@fluentui/react";
// Added by Gauri to fix dropdown position issue on 20 Nov 2025
import { DirectionalHint } from "@fluentui/react";
import { toast } from "react-toastify";

const CostDetailsForm = ({ formState1, handleSaveClick, roleOptions, acc, isEditMode, initialToDate  }) => {
  const [formState, setFormState] = useState({
    costCategory: "",
    costCategoryID: "",
    amount: "",
    costType: 0, // 0 = Fixed, 1 = Running
    fromDate: null,
    toDate: null,
    description: ""
  });

  // Sync formState1 with formState when formState1 is available
  useEffect(() => {
    if (formState1) {
      setFormState((prevState) => ({
        ...prevState,
        ...formState1
      }));
    }
  }, [formState1]);


  const handleCostTypeChange = (id) => {
    if (formState.costType !== id) {
      setFormState((prevState) => ({
        ...prevState,
        costType: id
      }));
    }
  };

  const handleFormChange = (field, value) => {
    setFormState((prevState) => {
      if (prevState[field] === value) return prevState; // Prevent unnecessary updates
      return { ...prevState, [field]: value };
    });
  };

 const formatDate = (date, format = "yyyy/mm/dd") => {
  if (!date) return "";

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  if (format === "yyyy/mm/dd") {
    return `${year}/${month}/${day}`;
  } else if (format === "mm/dd/yyyy") {
    return `${month}/${day}/${year}`;
  } else if (format === "dd/mm/yyyy") {
    return `${day}/${month}/${year}`;
  } else if (format === "dd-mm-yyyy") {
    return `${day}-${month}-${year}`;
  }

  return date;
};

  // Added by Gauri on 20 Nov 2025 - Common calloutProps for all dropdowns
  const commonCalloutProps = {
    // Use bottomAutoEdge which tries to open downward first, then automatically adjusts upward if needed
    directionalHint: DirectionalHint.bottomAutoEdge,
    // Set max height to prevent dropdown from being too large
    calloutMaxHeight: 250,
    // Ensure dropdown stays within viewport bounds
    bounds: typeof window !== 'undefined' ? {
      top: 0,
      left: 0,
      bottom: window.innerHeight,
      right: window.innerWidth
    } : undefined
  };

  
  return (
    <div id="CostDetailsTab1">
      {/* Save Button */}
      {acc[2]?.access !== 0 && (
        <div className="detailsubtabsbtn pb-1 text-end">
          <PrimaryButton
            text="Save"
            onClick={() => handleSaveClick({ formState1: formState, initialToDate: initialToDate })}
          />
        </div>
      )}

      {/* Mandatory Label */}
      <div className="col-sm-12 text-end form-group">
        <label className="form-label IM_label">
          (<span style={{ color: "red" }}>*</span> Mandatory)
        </label>
      </div>

      {/* Form Fields */}
      <div className="form-group row pt-1 mb-3 d-flex align-items-center">
        {/* Cost Category Field */}
        <div className="col-sm-4 form-group required">
          <label className="form-label IM_label text-end">
            Cost Category <span style={{ color: "red" }}>*</span>
          </label>
          {/* Modified by Gauri to fix dropdown position issue on 20 Nov 2025 */}
          <Dropdown
            placeholder="Select a category"
            id="select_Category"
            selectedKey={formState?.costCategoryID}
            options={[
              { key: "", text: "Select Cost Category" },
              ...roleOptions
            ]}
            onChange={(e, option) => handleFormChange("costCategoryID", option?.key)}
            calloutProps={commonCalloutProps}
          />
        </div>

        {/* Cost Type Radio Buttons */}
        <div className="col-sm-4 form-group required">
          <label className="form-label IM_label text-end">
            Cost Type <span style={{ color: "red" }}>*</span>
          </label>
          <div className="d-flex align-items-center">
            <label style={{ marginRight: "8px" }}>
              <input
                type="radio"
                name="costType"
                value="0"
                checked={formState.costType === 0}
                onChange={() => handleCostTypeChange(0)} // Only update when necessary
                style={{ marginRight: "5px" }}
              />
              Fixed
            </label>
            <label>
              <input
                type="radio"
                name="costType"
                value="1"
                checked={formState.costType === 1}
                onChange={() => handleCostTypeChange(1)} // Only update when necessary
                style={{ marginRight: "5px" }}
              />
              Running
            </label>
          </div>
        </div>

        {/* Amount Field */}
        <div className="col-sm-4 form-group required">
          <label className="IM_label text-end">
            Amount <span style={{ color: "red" }}>*</span>
          </label>
          <TextField
            value={formState.amount}
            onChange={(e) => {
              const newValue = e.target.value;

              // Regular expression to allow up to 9 digits before the decimal point and up to 2 digits after
              if (/^\d{0,9}(\.\d{0,2})?$/.test(newValue)) {
                handleFormChange("amount", newValue);
              }
            }}
            type="text"
            inputProps={{
              inputMode: "decimal" // Ensure appropriate keyboard for numeric input
            }}
            styles={{
              fieldGroup: { border: "1px solid #ced4da", borderRadius: "4px" },
              root: { marginTop: "8px" }
            }}
          />
        </div>
      </div>

      <div className="form-group row pt-1 mb-3 align-items-center">
        {/* From Date Field */}
        <div className="col-sm-4 form-group required">
          <label className="form-label IM_label text-end">
            From Date <span style={{ color: "red" }}>*</span>
          </label>
          <DatePicker
            placeholder="Select Date"
            value={formState.fromDate ? new Date(formState.fromDate) : null}
            onSelectDate={(date) => {
              if (date) {
                // Adjust for time zone offset
                const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                handleFormChange("fromDate", adjustedDate);
              } else {
                handleFormChange("fromDate", null);
              }
            }}
            disabled={isEditMode} // <-- Disable in edit mode
            required
          />
        </div>

        {/* To Date Field */}
        <div className="col-sm-4 form-group required">
          <label className="form-label IM_label text-end">
            To Date <span style={{ color: "red" }}>*</span>
          </label>
          <DatePicker
            placeholder="Select Date"
            value={formState.toDate ? new Date(formState.toDate) : null}
            onSelectDate={(date) => {
              if (date) {
                // Adjust for time zone offset
                const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                handleFormChange("toDate", adjustedDate);
              } else {
                handleFormChange("toDate", null);
              }
            }}
            required
          />
          {/* <DatePicker
            placeholder="Select Date"
            value={formState.toDate ? new Date(formState.toDate) : null}
            onSelectDate={(date) => {
              if (date) {
                const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

                if (formState.amount && formState.toDate && new Date(adjustedDate) < new Date(formState.toDate)) {
                  toast.error(`To Date should not be less than '${formatDate(formState.toDate)}'`);
                  return;
                }

                handleFormChange("toDate", adjustedDate);
              } else {
                handleFormChange("toDate", null);
              }
            }}
            required
          /> */}
        </div>

        {/* Description Field */}
        <div className="col-sm-4 form-group required">
          <label className="form-label IM_label text-end">
            Description<span style={{ color: "red" }}>*</span>
          </label>
          <TextField
            value={formState.description}
            onChange={(e) => handleFormChange("description", e.target.value)}
            multiline
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};

export default CostDetailsForm;
